/**
 * Page behaviors for the promo site (Claude Design "Hotline Site"):
 *   - sticky-nav "stuck" state,
 *   - rect-based scroll reveal (with a stalled-transition watchdog and a
 *     no-scroll fallback, per the design handoff),
 *   - install-snippet copy button,
 *   - subscribe form (client-side success state),
 *   - the "try it" nudge that hides past the hero / while the widget is open.
 *
 * Everything is exported and DOM-parameterised so it is unit-testable in jsdom.
 */

/** The exact one-line install snippet the copy button puts on the clipboard. */
export const INSTALL_SNIPPET =
  '<script type="module" src="https://cdn.hotline.red/hotline.js" ' +
  'data-public-key="pk_live_a3f8e1c92b4d76e0" data-mode="floating"></script>';

/** Toggle the nav's stuck (blurred, bordered) state from the scroll position. */
export function initNavStuck(win: Window = window, doc: Document = document): void {
  const nav = doc.getElementById('nav');
  if (!nav) return;
  const onScroll = (): void => {
    nav.classList.toggle('nav--stuck', win.scrollY > 12);
  };
  onScroll();
  win.addEventListener('scroll', onScroll, { passive: true });
}

/** Force an element visible if its entrance transition stalled at opacity 0. */
function forceVisible(el: HTMLElement, win: Window): void {
  if (parseFloat(win.getComputedStyle(el).opacity) < 0.99) {
    el.style.transition = 'none';
    el.style.opacity = '1';
    el.style.transform = 'none';
  }
}

/**
 * Rect-based scroll reveal. Reveals `.reveal` elements as they enter the
 * viewport; a watchdog force-shows any element whose transition froze, and a
 * timed fallback reveals everything if the window never scrolls (embedded
 * full-height contexts). Returns the reveal function for tests.
 */
export function initReveal(win: Window = window, doc: Document = document): () => void {
  const revealEls = Array.from(doc.querySelectorAll<HTMLElement>('.reveal'));
  const show = (el: HTMLElement): void => {
    if (el.classList.contains('in')) return;
    el.classList.add('in');
    win.setTimeout(() => forceVisible(el, win), 900);
  };
  const revealCheck = (): void => {
    const vh = win.innerHeight || doc.documentElement.clientHeight;
    for (const el of revealEls) {
      if (el.classList.contains('in')) continue;
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0) show(el);
    }
  };
  revealCheck();
  let scrolled = false;
  win.addEventListener(
    'scroll',
    () => {
      scrolled = true;
      revealCheck();
    },
    { passive: true },
  );
  win.addEventListener('resize', revealCheck);
  win.addEventListener('load', () => {
    revealCheck();
    win.requestAnimationFrame(revealCheck);
  });
  win.setTimeout(() => {
    if (!scrolled) revealEls.forEach(show);
  }, 1800);
  return revealCheck;
}

/** Wire the install-snippet copy button (label flips to "Copied!" briefly). */
export function initCopy(doc: Document = document, navigatorRef: Navigator = navigator): void {
  const copyBtn = doc.getElementById('copyBtn');
  const copyLbl = doc.getElementById('copyLbl');
  if (!copyBtn || !copyLbl) return;
  copyBtn.addEventListener('click', () => {
    void navigatorRef.clipboard?.writeText(INSTALL_SNIPPET);
    copyLbl.textContent = 'Copied!';
    setTimeout(() => {
      copyLbl.textContent = 'Copy';
    }, 1600);
  });
}

/** Subscribe form: swap to the success state on submit (client-side only). */
export function initSubscribe(doc: Document = document): void {
  const subForm = doc.getElementById('subForm');
  if (!subForm) return;
  subForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = (doc.getElementById('subEmail') as HTMLInputElement | null)?.value.trim();
    if (!email) return;
    subForm.outerHTML =
      '<div class="sub-done"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 6 9 17 4 12"/></svg> You’re on the list — see you in the Dispatch.</div>';
  });
}

/** Hide the "try it" nudge once the user scrolls past the hero. */
export function initNudge(win: Window = window, doc: Document = document): void {
  const tryNudge = doc.getElementById('tryNudge');
  const heroEl = doc.querySelector('.hero');
  if (!tryNudge || !heroEl) return;
  const nudgeCheck = (): void => {
    const past = heroEl.getBoundingClientRect().bottom < 80;
    tryNudge.style.visibility = past ? 'hidden' : 'visible';
  };
  nudgeCheck();
  win.addEventListener('scroll', nudgeCheck, { passive: true });
}
